import { Fragment, useEffect, useState } from "react";

import {
  Check,
  Close,
  DoNotDisturb,
  Create,
  Search,
  PendingActions,
} from "@mui/icons-material";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";

import {
  useAppSelector,
  useToolBoardingStatus,
  useTableauUserSiteRole,
  useTask,
  useUser,
  useContract,
} from "../../app/hooks";
import Error from "../common/Error";
import Loading from "../common/Loading";
import {
  Tool,
  BoardingStatus,
  ToolConfig,
  Contract,
} from "../../app/backendTypes";
import BoardingDialog from "../common/tools/BoardingDialog";
import JiraProjectsDialog from "../common/tools/Jira/JiraProjectsDialog";
import Request from "../common/tasks/TaskView";
import Avatar from "../common/Avatar";
import BlackDuckProjectsDialog from "../common/tools/BlackDuck/BlackDuckProjectsDialog";
import PolarisGroupsDialog from "../common/tools/Polaris/PolarisGroupsDialog";
import TableauSiteRoleDialog from "../common/tools/Tableau/TableauSiteRoleDialog";
import LoadingSpan from "../common/LoadingSpan";
import SupportCard from "../common/support/SupportCard";

export default function ToolComponent(props: { config: ToolConfig }) {
  const tool = props.config.kind as Tool;
  const toolItem = props.config.item.tool;
  const customLinks = props.config.item.links || [];
  const userId = useAppSelector((state) => state.auth.userId);
  const { user } = useUser(userId);
  const { contract } = useContract(toolItem?.contract_id?.toString());

  return (
    <>
      <Box sx={{ display: "flex", gap: 2 }}>
        <Avatar
          alt={toolItem?.display_name || contract?.name || tool}
          src={toolItem?.avatar || contract?.avatar}
        />
        <Typography variant="h4">
          {toolItem?.display_name || contract?.name || tool}
        </Typography>
      </Box>

      {toolItem !== undefined && (
        <>
          <Card sx={{ width: "100%", maxWidth: "sm" }}>
            <CardHeader
              title={toolItem.short_desc || contract?.short_desc}
              titleTypographyProps={{ color: "primary" }}
            />
            <CardContent>
              <Typography>
                {toolItem.long_desc || contract?.long_desc}
              </Typography>

              <br />

              <Typography>
                <Typography component="span" color="text.secondary">
                  Pricing:{" "}
                </Typography>
                {toolItem.pricing.desc || contract?.pricing}
              </Typography>

              <br />

              <Button
                href={toolItem.links.main}
                target="_blank"
                color="secondary"
                variant="outlined"
              >{`Go to ${tool}`}</Button>

              {customLinks.length > 0 && (
                <>
                  <br />

                  <Box sx={{ display: "flex", gap: 1 }}>
                    {customLinks.map((link, i) => (
                      <Button
                        key={i}
                        href={link.url}
                        target="_blank"
                        color="secondary"
                      >
                        {link.name}
                      </Button>
                    ))}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>

          <AccessManagement tool={tool} />

          {contract !== null && <ContractDetails contract={contract} />}
          {user !== null && (
            <SupportCard
              resource={{
                kind: "Tool",
                inner: { id: tool },
              }}
              user={user}
            />
          )}
        </>
      )}
    </>
  );
}

export function ContractDetails(props: { contract: Contract }) {
  const { contract } = props;

  return (
    <Card sx={{ width: "100%", maxWidth: "sm" }}>
      <CardHeader
        title="Contract details"
        titleTypographyProps={{ color: "primary" }}
      />
      <CardContent>
        <Typography>
          <Typography component="span" color="text.secondary">
            Partner:{" "}
          </Typography>
          {contract.partner}
        </Typography>

        <Typography>
          <Typography component="span" color="text.secondary">
            Responsible:{" "}
          </Typography>
          {contract.owner}
        </Typography>

        <Typography>
          <Typography component="span" color="text.secondary">
            Department:{" "}
          </Typography>
          {contract.department}
        </Typography>

        {contract.expiration_date !== undefined &&
          contract.expiration_date !== null && (
            <Typography>
              <Typography component="span" color="text.secondary">
                Renewal:{" "}
              </Typography>
              {contract.expiration_date}
            </Typography>
          )}
      </CardContent>
    </Card>
  );
}

function AccessManagement(props: { tool: Tool }) {
  const { tool } = props;

  const userId = useAppSelector((app) => app.auth.userId);

  const { status, loading, error, reload } = useToolBoardingStatus(
    tool,
    userId
  );

  const { element: pendingElement, action: pendingAction } = usePendingParts(
    status?.task_id,
    reload
  );

  const [refresh, setRefresh] = useState(false);

  const elements = [];
  const actions = [];

  if (loading) elements.push(<Loading />);
  else if (error) elements.push(<Error error={error} />);
  else if (status !== null) {
    if (tool === Tool.Lucid) {
      elements.push(<LucidBoardingElement status={status} />);
    } else {
      elements.push(<BoardingElement status={status} />);
    }

    if (!status.task_id && tool !== Tool.Lucid && !status.value)
      actions.push(<RequestOnboardingButton tool={tool} callback={reload} />);

    if (status.task_id) {
      elements.push(pendingElement);
      actions.push(pendingAction);
    }

    switch (tool) {
      case Tool.BlackDuck: {
        actions.push(<BlackDuckProjectsAction status={status} />);
        break;
      }

      case Tool.Jira: {
        actions.push(<JiraProjectsAction status={status} />);
        break;
      }

      case Tool.Polaris: {
        actions.push(<PolarisGroupsAction status={status} />);
        break;
      }

      case Tool.Tableau: {
        elements.push(
          <TableauSiteRoleElement status={status} refresh={refresh} />
        );

        // actions.push(<TableauProjectsAction status={status} />);

        actions.push(
          <TableauSiteRoleAction
            status={status}
            onRoleChange={() => setRefresh(!refresh)}
          />
        );

        break;
      }

      case Tool.Lucid: {
        if (!status.value && !status.task_id) {
          actions.push(<RequestLucidOnboardingButton callback={reload} />);
        }
        break;
      }
    }
  }

  return (
    <Card sx={{ width: "100%", maxWidth: "sm" }}>
      <CardHeader
        subheader="Manage access"
        subheaderTypographyProps={{ color: "primary", fontWeight: "bold" }}
      />
      <CardContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {elements.map((element, i) => (
            <Fragment key={i}>{element}</Fragment>
          ))}
        </Box>

        <br />

        <Box sx={{ display: "flex", gap: 1 }}>
          {actions.map((a, i) => (
            <Fragment key={i}>{a}</Fragment>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

export function BoardingElement(props: { status: BoardingStatus }) {
  let element = <></>;
  if (props.status.value) {
    element = (
      <>
        <Typography>Onboarded</Typography>
        <Check fontSize="small" color="success" />
      </>
    );
  } else {
    element = (
      <>
        <Typography>Offboarded</Typography>
        <DoNotDisturb fontSize="small" color="error" />
      </>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <Typography component="span" color="text.secondary">
        Status:{" "}
      </Typography>

      {element}
    </Box>
  );
}

export function LucidBoardingElement(props: { status: BoardingStatus }) {
  let element = <></>;
  if (props.status.value) {
    element = (
      <>
        <Typography>You have write rights</Typography>
        <Create fontSize="small" color="success" />
      </>
    );
  } else {
    element = (
      <>
        <Typography>You only have read rights</Typography>
        <Search fontSize="small" color="error" />
      </>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <Typography component="span" color="text.secondary">
        Status:{" "}
      </Typography>

      {element}
    </Box>
  );
}

export function usePendingParts(
  task_id: string | undefined,
  callback: () => void
) {
  let element = <></>;
  let action = <></>;

  const [open, setOpen] = useState(false);

  const { task, loading, error } = useTask(task_id);

  useEffect(() => {
    if (task?.status.kind === "Done" || task?.status.kind === "Cancelled")
      callback();
  }, [callback, task]);

  if (loading) element = <Loading />;
  else if (error) element = <Error error={error} />;
  else if (task !== null) {
    element = (
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <Typography component="span" color="text.secondary">
          Pending request:{" "}
        </Typography>
        Access
        <PendingActions fontSize="small" color="warning" />
      </Box>
    );

    action = (
      <>
        <Button
          color="secondary"
          variant="outlined"
          onClick={() => setOpen(true)}
        >
          See Access Request
        </Button>

        <Dialog open={open}>
          <Request
            task={task}
            callback={callback}
            expanded
            close={
              <IconButton onClick={() => setOpen(false)}>
                <Close />
              </IconButton>
            }
          />
        </Dialog>
      </>
    );
  }

  return { element, action };
}

function RequestOnboardingButton(props: { tool: Tool; callback: () => void }) {
  const { tool, callback } = props;

  const userId = useAppSelector((state) => state.auth.userId);
  const { user } = useUser(userId);

  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        color="secondary"
        onClick={() => setOpen(true)}
        variant="outlined"
      >
        Request Access
      </Button>
      {user !== null && (
        <BoardingDialog
          onboarded={false}
          open={open}
          setOpen={setOpen}
          tool={tool}
          user={user}
          callback={callback}
        />
      )}
    </>
  );
}

function RequestLucidOnboardingButton(props: { callback: () => void }) {
  const { callback } = props;

  const userId = useAppSelector((state) => state.auth.userId);
  const { user } = useUser(userId);

  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        color="secondary"
        onClick={() => setOpen(true)}
        variant="outlined"
      >
        Request Write Access
      </Button>
      {user !== null && (
        <BoardingDialog
          onboarded={false}
          open={open}
          setOpen={setOpen}
          tool={Tool.Lucid}
          user={user}
          callback={callback}
        />
      )}
    </>
  );
}

function BlackDuckProjectsAction(props: { status: BoardingStatus }) {
  const { status } = props;

  const [open, setOpen] = useState(false);

  const userId = useAppSelector((state) => state.auth.userId);
  const { user } = useUser(userId);

  let element = <></>;

  if (status.value) {
    element = (
      <>
        <Button
          color="secondary"
          variant="outlined"
          onClick={() => setOpen(true)}
        >
          Get access to projects
        </Button>
        {user !== null && (
          <BlackDuckProjectsDialog open={open} setOpen={setOpen} user={user} />
        )}
      </>
    );
  }

  return element;
}

function JiraProjectsAction(props: { status: BoardingStatus }) {
  const { status } = props;

  const [open, setOpen] = useState(false);

  const userId = useAppSelector((state) => state.auth.userId);
  const { user } = useUser(userId);

  let element = <></>;

  if (status.value) {
    element = (
      <>
        <Button
          color="secondary"
          variant="outlined"
          onClick={() => setOpen(true)}
        >
          Get access to projects
        </Button>
        {user !== null && (
          <JiraProjectsDialog open={open} setOpen={setOpen} user={user} />
        )}
      </>
    );
  }

  return element;
}

function PolarisGroupsAction(props: { status: BoardingStatus }) {
  const { status } = props;

  const [open, setOpen] = useState(false);

  const userId = useAppSelector((state) => state.auth.userId);
  const { user } = useUser(userId);

  let element = <></>;

  if (status.value) {
    element = (
      <>
        <Button
          color="secondary"
          variant="outlined"
          onClick={() => setOpen(true)}
        >
          Get access to groups
        </Button>
        {user !== null && (
          <PolarisGroupsDialog open={open} setOpen={setOpen} user={user} />
        )}
      </>
    );
  }

  return element;
}

function TableauSiteRoleElement(props: {
  status: BoardingStatus;
  refresh: boolean;
}) {
  const { status, refresh } = props;

  const userId = useAppSelector((state) => state.auth.userId);

  const { role, error, loading, reload } = useTableauUserSiteRole(userId);

  useEffect(() => {
    reload();
  }, [refresh, reload]);
  let element = <></>;

  if (status.value) {
    if (loading) element = <LoadingSpan />;
    else if (error) element = <Error error={error} />;
    else if (role !== null) {
      element = <>{role}</>;
    }
  }

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <Typography component="span" color="text.secondary">
        Site role:{" "}
      </Typography>

      {element}
    </Box>
  );
}

function TableauSiteRoleAction(props: {
  status: BoardingStatus;
  onRoleChange: () => void;
}) {
  const { status, onRoleChange } = props;

  const userId = useAppSelector((state) => state.auth.userId);
  const { user } = useUser(userId);

  const [open, setOpen] = useState(false);

  let element = <></>;

  if (status.value) {
    element = (
      <Button color="primary" variant="outlined" onClick={() => setOpen(true)}>
        Request New Site Role
      </Button>
    );
  }

  return (
    <>
      {element}

      {user !== null && (
        <TableauSiteRoleDialog
          open={open}
          setOpen={setOpen}
          user={user}
          onRoleChange={onRoleChange}
        />
      )}
    </>
  );
}

// function TableauProjectsAction(props: { status: BoardingStatus }) {
//   const { status } = props;

//   const userId = useAppSelector((state) => state.auth.userId);
//   const { user } = useUser(userId);

//   const [open, setOpen] = useState(false);

//   let element = <></>;

//   if (status.value) {
//     element = (
//       <Button color="primary" variant="outlined" onClick={() => setOpen(true)}>
//         Request Access to Projects
//       </Button>
//     );
//   }

//   return (
//     <>
//       {element}

//       {user !== null && (
//         <TableauProjectsDialog open={open} setOpen={setOpen} user={user} />
//       )}
//     </>
//   );
// }
